import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  Pagination,
  ContainerAvatar,
  SimpleTable,
  Checkbox,
  TableIconsCell,
  Button,
  repairCreateInvoiceModal,
  repairSetUpdateMultipleModal,
  enqueueSnackbar,
} from '../../design-system';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { momentTextFormat, showToRoles } from '../../utils';
import {
  useStatesWithFilterCounter,
  useSort,
  usePaginationState,
  useRepairsGroupQuery,
  useConvertCurrencyFcQuery,
} from '../../hooks';
import { TABLE, MESSAGES } from '../../const';
import { i18n } from '../../i18n';
import { RepairGetAllGroupQuery } from '../../types';

interface CheckboxActionsProps {
  data: RepairGetAllGroupQuery['data'];
  refetch: () => void;
  checkIndexs: number[];
  setCheckIndexs: (indexes: number[]) => void;
}

export const CheckboxActions = ({
  data,
  checkIndexs,
  setCheckIndexs,
  refetch,
}: CheckboxActionsProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const filteredCreateInvoiceData = data
    .filter((_: any, i: number) => checkIndexs.includes(i))
    .filter((item) => item.customRepair.uncreatedInvoiceCount > 0);

  const handleClickAction = (val: any) => {
    if (val === 'create-invoice') {
      repairCreateInvoiceModal.data = filteredCreateInvoiceData.map((item) => ({
        id: item.id,
        number: item.number,
      }));

      repairCreateInvoiceModal.submitClick = () => {
        setCheckIndexs([]);
        refetch();
      };
      repairCreateInvoiceModal.open();
    }
  };

  if (!checkIndexs.length) return <></>;

  return (
    <>
      {showToRoles('admin', user) && !!filteredCreateInvoiceData.length && (
        <Button
          size={'medium'}
          data-color={'white'}
          onClick={() => handleClickAction('create-invoice')}
        >
          {i18n.t('button.createInvoice')}
        </Button>
      )}
    </>
  );
};

export const RepairsGroupBody = ({
  setData,
  setTotalCount,
  checkIndexs,
  setCheckIndexs,
  handleCheckboxChange,
  setOnAddRepairClick,
}: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const isViewer = showToRoles(['YARD_COMPANY_VIEWER'], user);
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    country: '',
    city: '',
    yardId: '',
    clientId: '',
    number: '',
  });
  const { sortField, sortDirection, handleSort } = useSort({
    sortField: 'createdAt',
    sortDirection: 'desc',
  });

  const convertCurrency = useConvertCurrencyFcQuery();
  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useRepairsGroupQuery({
    ...filteredStates,
    take,
    skip,
    sortField,
    sortDirection,
    convertCurrency,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
    setData(data.data);
  }, [isFetching, refetch]);

  const handleOnPageChange = (e: any, page: number) => {
    if (checkIndexs.length) setCheckIndexs([]);
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleRowClick = (to: string) => {
    if (checkIndexs.length) return;
    navigate(to);
  };

  const handleAddRepair = useCallback(() => {
    repairSetUpdateMultipleModal.showContainerSelect = true;
    repairSetUpdateMultipleModal.containerId = '';
    repairSetUpdateMultipleModal.number = '';
    repairSetUpdateMultipleModal.action = 'repair';

    repairSetUpdateMultipleModal.data = [
      {
        id: '',
        price: '0',
        expectedDurationDays: 0,
        description: '',
        beforeImages: [],
      },
    ];

    repairSetUpdateMultipleModal.submitClick = () => {
      enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.REPAIR, 'success');
      refetch();
    };
    repairSetUpdateMultipleModal.open();
  }, [refetch]);

  useEffect(() => {
    setOnAddRepairClick?.(() => handleAddRepair);
  }, [handleAddRepair, setOnAddRepairClick]);

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      checkboxActions={
        isViewer
          ? undefined
          : (data) => (
              <CheckboxActions
                data={data}
                checkIndexs={checkIndexs}
                setCheckIndexs={setCheckIndexs}
                refetch={refetch}
              />
            )
      }
      tableRowProps={(item, i) => ({
        key: i,
        onClick: () => handleRowClick(`/dashboard/repairs/${item.id}`),
      })}
      columns={[
        {
          column: 1,
          head: () => (
            <TableHeadCell align={'left'}>
              {!isViewer && (
                <Checkbox
                  indeterminate={!!checkIndexs.length && checkIndexs.length !== data.data.length}
                  checked={checkIndexs.length === data.data.length}
                  onChange={() => handleCheckboxChange()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TableHeadCell>
          ),
          body: (_, i) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!isViewer && (
                <Checkbox
                  checked={checkIndexs.includes(i)}
                  onChange={() => handleCheckboxChange(i)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </TableIconsCell>
          ),
          isHidden: () => showToRoles('client', user),
        },
        {
          column: 1,
          headText: () => i18n.t('label.photo'),
          body: (item) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar images={item.images} />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.client'),
          bodyText: (item) => item.clientYard?.client?.code,
          isHidden: () => !showToRoles('admin', user),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (item) => item.number,
        },
        {
          head: () => (
            <TableHeadCell
              align={'left'}
              columnIdx={'createdAt'}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            >
              {i18n.t('label.createdAt')}
            </TableHeadCell>
          ),
          bodyText: (item) => (item.createdAt ? momentTextFormat(item.createdAt, user) : ''),
        },
        {
          head: () => (
            <TableHeadCell id={'column-pending-count'} align={'left'}>
              {i18n.t('label.pendingCount')}
            </TableHeadCell>
          ),
          bodyText: (item) => item.customRepair.pendingCount,
        },
        {
          head: () => (
            <TableHeadCell id={'column-active-count'} align={'left'}>
              {i18n.t('label.activeCount')}
            </TableHeadCell>
          ),
          bodyText: (item) => item.customRepair.activeCount,
        },
        {
          head: () => (
            <TableHeadCell id={'column-uncreated-invoice-count'} align={'left'}>
              {i18n.t('label.uncreatedInvoiceCount')}
            </TableHeadCell>
          ),
          bodyText: (item) => item.customRepair.uncreatedInvoiceCount,
        },
        {
          headText: () => i18n.t('label.price'),
          bodyText: (item) => item.customRepair.price,
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
